import { useState } from 'react';  
import { Camera, Download, Search, User } from 'lucide-react';  
import Header from '../components/layout/Header';  
import { Button } from '../components/ui/button';  
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';  
import { Input } from '../components/ui/input';  
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';  
import { Label } from '../components/ui/label';  
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';  
import { useToast } from '../components/ui/use-toast';  
import { useIsMobile } from '../hooks/use-mobile';  
import jsPDF from 'jspdf';  
import html2canvas from 'html2canvas';  

// Sample ID Card data  
const sampleIdCards = [  
  {  
    id: '1',  
    studentName: 'Jennifer Wilson',  
    studentId: 'STU-2023-001',  
    course: 'Computer Science',  
    validUntil: '2024-12-31',  
    photo: 'https://media.istockphoto.com/id/1281240685/photo/portrait-of-a-crawling-baby.jpg?s=612x612&w=0&k=20&c=DC3oVtzBPm2_jby_UFDE5LBGgqSyr5MM72Yi0090ymU=', // Base64 string instead of URL  
    issueDate: '2023-01-15',  
  },  
  {  
    id: '2',  
    studentName: 'Michael Thompson',  
    studentId: 'STU-2023-002',  
    course: 'Business Administration',  
    validUntil: '2024-12-31',  
    photo: 'https://i.pravatar.cc/150?img=3',  
    issueDate: '2023-01-20',  
  },  
  {  
    id: '3',  
    studentName: 'Sarah Johnson',  
    studentId: 'STU-2023-003',  
    course: 'Graphic Design',  
    validUntil: '2024-12-31',  
    photo: 'https://i.pravatar.cc/150?img=4',  
    issueDate: '2023-01-25',  
  },  
];  

const IDCard = () => {  
  const { toast } = useToast();  
  const isMobile = useIsMobile();  
  const [searchTerm, setSearchTerm] = useState('');  
  const [idCards, setIdCards] = useState(sampleIdCards);  
  const [selectedCard, setSelectedCard] = useState(null);  
  const [formData, setFormData] = useState({  
    studentName: '',  
    studentId: '',  
    course: '',  
    validUntil: '',  
    photo: null,  
    photoPreview: null,  
  });  

  const filteredCards = idCards.filter(card =>   
    card.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||  
    card.studentId.toLowerCase().includes(searchTerm.toLowerCase()) ||  
    card.course.toLowerCase().includes(searchTerm.toLowerCase())  
  );  

  const handleSearch = (e) => {  
    setSearchTerm(e.target.value);  
  };  

  const handleInputChange = (e) => {  
    const { name, value } = e.target;  
    setFormData(prev => ({ ...prev, [name]: value }));  
  };  

  const handleSelectChange = (name, value) => {  
    setFormData(prev => ({ ...prev, [name]: value }));  
  };  

  const handlePhotoChange = (e) => {  
    const file = e.target.files[0];  
    if (file) {  
      const reader = new FileReader();  
      reader.onloadend = () => {  
        setFormData(prev => ({   
          ...prev,   
          photo: file,  
          photoPreview: reader.result   
        }));  
      };  
      reader.readAsDataURL(file);  
    }  
  };  

  const handleGenerateID = (e) => {  
    e.preventDefault();  
    
    // Generate a new ID card  
    const newCard = {  
      id: `${idCards.length + 1}`,  
      studentName: formData.studentName,  
      studentId: formData.studentId,  
      course: formData.course,  
      validUntil: formData.validUntil,  
      photo: formData.photoPreview || 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAA,...', // Default to local base64  
      issueDate: new Date().toISOString().split('T')[0],  
    };  
    
    setIdCards([...idCards, newCard]);  
    toast({  
      title: "ID Card Generated",  
      description: `ID Card for ${formData.studentName} has been created successfully.`,  
    });  
    
    // Reset form  
    setFormData({  
      studentName: '',  
      studentId: '',  
      course: '',  
      validUntil: '',  
      photo: null,  
      photoPreview: null,  
    });  
  };  

  const downloadIDCard = (card) => {  
    const doc = new jsPDF();  
  
    const img = new Image();  
    img.crossOrigin = "Anonymous"; // This is needed for CORS if the image allows it  
    img.src = card.photo;  
  
    img.onload = () => {  
      // Once the image is loaded, we can draw it on the PDF  
      doc.addImage(img, 'PNG', 10, 20, 50, 65);  
  
      // Add student info  
      doc.setFont("Helvetica", "normal");  
      doc.setFontSize(12);  
      doc.text(`Name: ${card.studentName}`, 70, 30);  
      doc.text(`Student ID: ${card.studentId}`, 70, 40);  
      doc.text(`Course: ${card.course}`, 70, 50);  
      doc.text(`Valid Until: ${card.validUntil}`, 70, 60);  
      doc.text(`Issued: ${card.issueDate}`, 70, 70);  
  
      // Save the PDF  
      doc.save(`${card.studentName}_ID_Card.pdf`);  
      toast({  
        title: "Download Successful",  
        description: `Your ID card for ${card.studentName} has been downloaded.`,  
      });  
    };  
  
    img.onerror = () => {  
      toast({  
        title: "Error",  
        description: "Could not load the image. Please check the image URL.",  
      });  
    };  
  };   
  

  return (  
    <div className="h-full flex flex-col bg-gray-50">  
      <Header title="ID Card" subtitle="Generate and manage student ID cards" />  
      
      <div className="flex-1 p-4 md:p-8 animate-fade-in overflow-auto">  
        <Tabs defaultValue="previous" className="w-full">  
          <TabsList className="grid w-full max-w-md mx-auto mb-6 grid-cols-2">  
            <TabsTrigger value="previous">Previous ID Cards</TabsTrigger>  
            <TabsTrigger value="generate">Generate New</TabsTrigger>  
          </TabsList>  
          
          <TabsContent value="previous" className="space-y-4">  
            <div className="flex justify-between items-center mb-4 flex-col sm:flex-row gap-3">  
              <div className="relative w-full sm:w-64">  
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />  
                <Input  
                  placeholder="Search ID cards..."  
                  className="pl-10"  
                  value={searchTerm}  
                  onChange={handleSearch}  
                />  
              </div>  
            </div>  
            
            {filteredCards.length === 0 ? (  
              <div className="text-center py-10">  
                <User size={48} className="mx-auto text-gray-300 mb-3" />  
                <p className="text-gray-500">No ID cards found with the current search criteria.</p>  
              </div>  
            ) : (  
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">  
                {filteredCards.map(card => (  
                  <Card   
                    key={card.id}   
                    className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer"  
                    onClick={() => setSelectedCard(card)}  
                  >  
                    <CardHeader className="p-4 bg-bytesplash-blue text-white">  
                      <div className="flex justify-between items-start">  
                        <CardTitle className="text-lg">ByteSplash</CardTitle>  
                        <p className="text-xs opacity-75">Student ID Card</p>  
                      </div>  
                    </CardHeader>  
                    <CardContent className="p-4">  
                      <div className="flex gap-4">  
                        <div className="w-20 h-24 bg-gray-100 rounded overflow-hidden">  
                          <img   
                            src={card.photo}   
                            alt={card.studentName}   
                            className="w-full h-full object-cover"  
                          />  
                        </div>  
                        <div className="flex-1">  
                          <p className="font-semibold text-base">{card.studentName}</p>  
                          <p className="text-xs text-gray-500 mt-1">ID: {card.studentId}</p>  
                          <p className="text-xs text-gray-500 mt-1">Course: {card.course}</p>  
                          <div className="flex justify-between mt-3">  
                            <div className="text-xs">  
                              <p className="text-gray-500">Issue Date</p>  
                              <p>{card.issueDate}</p>  
                            </div>  
                            <div className="text-xs">  
                              <p className="text-gray-500">Valid Until</p>  
                              <p>{card.validUntil}</p>  
                            </div>  
                          </div>  
                        </div>  
                      </div>  
                      <div className="mt-4 flex justify-end">  
                        <Button   
                          variant="outline"   
                          size="sm"   
                          className="text-bytesplash-blue"  
                          onClick={(e) => {  
                            e.stopPropagation();  
                            downloadIDCard(card);  // Pass the selected card to the download function  
                          }}  
                        >  
                          <Download size={14} className="mr-1" />  
                          Download  
                        </Button>  
                      </div>  
                    </CardContent>  
                  </Card>  
                ))}  
              </div>  
            )}  
          </TabsContent>  
          
          <TabsContent value="generate">  
            <Card className="max-w -2xl mx-auto">  
              <CardHeader>  
                <CardTitle className="text-bytesplash-blue">Generate New ID Card</CardTitle>  
              </CardHeader>  
              <CardContent>  
                <form onSubmit={handleGenerateID} className="space-y-6">  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">  
                    <div className="space-y-2">  
                      <Label htmlFor="studentName">Student Name</Label>  
                      <Input  
                        id="studentName"  
                        name="studentName"  
                        value={formData.studentName}  
                        onChange={handleInputChange}  
                        placeholder="Enter student name"  
                        required  
                      />  
                    </div>  
                    
                    <div className="space-y-2">  
                      <Label htmlFor="studentId">Student ID</Label>  
                      <Input  
                        id="studentId"  
                        name="studentId"  
                        value={formData.studentId}  
                        onChange={handleInputChange}  
                        placeholder="e.g., STU-2023-004"  
                        required  
                      />  
                    </div>  
                    
                    <div className="space-y-2">  
                      <Label htmlFor="course">Course</Label>  
                      <Select  
                        value={formData.course}  
                        onValueChange={(value) => handleSelectChange('course', value)}  
                      >  
                        <SelectTrigger>  
                          <SelectValue placeholder="Select course" />  
                        </SelectTrigger>  
                        <SelectContent>  
                          <SelectItem value="Computer Science">Computer Science</SelectItem>  
                          <SelectItem value="Business Administration">Business Administration</SelectItem>  
                          <SelectItem value="Graphic Design">Graphic Design</SelectItem>  
                          <SelectItem value="Digital Marketing">Digital Marketing</SelectItem>  
                          <SelectItem value="Data Science">Data Science</SelectItem>  
                        </SelectContent>  
                      </Select>  
                    </div>  
                    
                    <div className="space-y-2">  
                      <Label htmlFor="validUntil">Valid Until</Label>  
                      <Input  
                        id="validUntil"  
                        name="validUntil"  
                        type="date"  
                        value={formData.validUntil}  
                        onChange={handleInputChange}  
                        required  
                      />  
                    </div>  
                  </div>  
                  
                  <div className="space-y-2">  
                    <Label>Student Photo</Label>  
                    <div className="flex items-start gap-4 flex-col sm:flex-row">  
                      <div className="w-32 h-40 bg-gray-100 rounded flex flex-col items-center justify-center overflow-hidden">  
                        {formData.photoPreview ? (  
                          <img  
                            src={formData.photoPreview}  
                            alt="Preview"  
                            className="w-full h-full object-cover"  
                          />  
                        ) : (  
                          <div className="flex flex-col items-center justify-center p-4">  
                            <Camera className="h-10 w-10 text-gray-400 mb-2" />  
                            <span className="text-xs text-gray-500 text-center">Upload photo</span>  
                          </div>  
                        )}  
                      </div>  
                      <div className="flex-1">  
                        <div className="space-y-2">  
                          <input  
                            id="photo"  
                            name="photo"  
                            type="file"  
                            accept="image/*"  
                            className="hidden"  
                            onChange={handlePhotoChange}  
                          />  
                          <Button  
                            type="button"  
                            variant="outline"  
                            onClick={() => document.getElementById('photo').click()}  
                            className="w-full sm:w-auto"  
                          >  
                            <Camera className="mr-2 h-4 w-4" />  
                            Upload Photo  
                          </Button>  
                          <p className="text-xs text-gray-500 mt-2">  
                            Please upload a clear front-facing photo. Maximum size: 2MB  
                          </p>  
                        </div>  
                      </div>  
                    </div>  
                  </div>  
                  
                  <div className="flex justify-end gap-3">  
                    <Button  
                      type="button"  
                      variant="outline"  
                      onClick={() => setFormData({  
                        studentName: '',  
                        studentId: '',  
                        course: '',  
                        validUntil: '',  
                        photo: null,  
                        photoPreview: null,  
                      })}  
                    >  
                      Reset  
                    </Button>  
                    <Button type="submit">Generate ID Card</Button>  
                  </div>  
                </form>  
              </CardContent>  
            </Card>  
          </TabsContent>  
        </Tabs>  
      </div>  
    </div>  
  );  
};  

export default IDCard;